import Image, { type ImageLoader, type StaticImageData } from "next/image";
import type { CSSProperties } from "react";

/**
 * Every backend ad slot has one fixed shape, so ads that sit together line up:
 * the GLOBAL banner is 4:1 (and, beside its square, takes the square's height),
 * the square beside it and every SQUARE side ad are 6:5 (the 300x250 rectangle),
 * a DOUBLE side ad is 1:2 (the 300x600 half page). A creative in another shape
 * is never cropped: it is contained over a darkened, blurred copy of itself,
 * like the article thumbnails. Both layers use the same src, loader and
 * `sizes`, so they resolve to one URL and the browser downloads it once. The
 * frame reserves its box before the image arrives, so nothing shifts.
 *
 * Recommended creative sizes (shown in the dashboard's ad forms):
 * banner 1600x400, rectangle 600x500, half page 600x1200.
 */
export type AdShape = "banner" | "rect" | "double";

const SHAPE_CLASS: Record<AdShape, string> = {
  banner: "aspect-[4/1]",
  rect: "aspect-[6/5]",
  double: "aspect-[1/2]",
};

type Props = {
  src: string | StaticImageData;
  href: string;
  title?: string | null;
  shape: AdShape;
  sizes: string;
  loading?: "eager" | "lazy";
  loader?: ImageLoader;
  /**
   * Fill the parent instead of keeping the shape's ratio (the banner beside its
   * square, which sets the row's height). Pass the responsive classes that
   * switch it on, e.g. "md:absolute md:inset-0 md:aspect-auto".
   */
  fillClassName?: string;
  className?: string;
  style?: CSSProperties;
};

const AdCreative = ({ src, href, title, shape, sizes, loading = "lazy", loader, fillClassName = "", className = "", style }: Props) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    aria-label={title || "Advertisement"}
    className={`relative block w-full overflow-hidden rounded-[4px] bg-stage-panel ${SHAPE_CLASS[shape]} ${fillClassName} ${className}`}
    style={style}
  >
    <Image
      src={src}
      loader={loader}
      alt=""
      aria-hidden="true"
      fill
      sizes={sizes}
      loading={loading}
      className="scale-110 object-cover blur-2xl brightness-[0.55]"
    />
    <Image
      src={src}
      loader={loader}
      alt={title || "Advertisement"}
      fill
      sizes={sizes}
      loading={loading}
      className="object-contain"
    />
  </a>
);

export default AdCreative;
